import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES } from '../data/countries';

type CountryComboboxProps = {
  id: string;
  name: string;
  value: string;
  onChange: (country: string) => void;
  required?: boolean;
};

export function CountryCombobox({
  id,
  name,
  value,
  onChange,
  required,
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.toLowerCase().includes(q));
  }, [query]);

  const displayValue = open ? query : value;

  return (
    <div ref={rootRef} className="relative">
      <input type="hidden" name={name} value={value} />
      <input
        id={id}
        type="text"
        autoComplete="off"
        required={required && !value}
        value={displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
          onChange('');
        }}
        onFocus={() => {
          setOpen(true);
          setQuery(value);
        }}
        placeholder="Search or select country"
        className="w-full rounded-lg border border-[#E4E6EB] bg-white px-4 py-3 text-[17px] text-[#1C1E21] outline-none transition focus:border-[#0064E0]"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        role="combobox"
      />
      {open && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-[#E4E6EB] bg-white py-2 shadow-[var(--shadow-lg)]"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-2 text-sm text-[#65676B]">No matches</li>
          ) : (
            filtered.map((c) => (
              <li key={c} role="option" aria-selected={c === value}>
                <button
                  type="button"
                  className="w-full px-4 py-2.5 text-left text-[15px] text-[#1C1E21] hover:bg-[#F0F2F5]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(c);
                    setQuery(c);
                    setOpen(false);
                  }}
                >
                  {c}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
